<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  csvErrors: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['cancel']);

const { t } = useI18n();

const formatLineNumbers = lines => {
  if (!lines || lines.length === 0) return '';

  const lineWord =
    lines.length === 1
      ? t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.LINE')
      : t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.LINES');

  return `(${lineWord} ${lines.join(', ')})`;
};

const errorDetails = computed(() => {
  if (!props.csvErrors) return [];

  const details = [];
  const { errors, error_messages } = props.csvErrors;

  if (errors.missing_phone_number_header) {
    details.push({
      message: error_messages.missing_phone_number_header,
      items: [],
    });
  }

  if (errors.invalid_phone_numbers) {
    details.push({
      message: error_messages.invalid_phone_numbers,
      items: Object.entries(errors.invalid_phone_numbers).map(
        ([phone, lines]) => ({
          phone,
          lines,
        })
      ),
    });
  }

  if (errors.duplicate_phone_numbers) {
    details.push({
      message: error_messages.duplicate_phone_numbers,
      items: Object.entries(errors.duplicate_phone_numbers).map(
        ([phone, lines]) => ({
          phone,
          lines,
        })
      ),
    });
  }

  return details;
});

const formatErrorsAsText = () => {
  if (!props.csvErrors) return '';

  const { error_messages, errors } = props.csvErrors;
  let text = `${t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.TITLE')}:\n\n`;

  if (errors.missing_phone_number_header) {
    text += `- ${error_messages.missing_phone_number_header}\n\n`;
  }

  if (errors.invalid_phone_numbers) {
    text += `- ${error_messages.invalid_phone_numbers}\n`;
    Object.entries(errors.invalid_phone_numbers).forEach(([phone, lines]) => {
      const lineText = formatLineNumbers(lines);
      text += `  ${phone} ${lineText}\n`;
    });
    text += '\n';
  }

  if (errors.duplicate_phone_numbers) {
    text += `- ${error_messages.duplicate_phone_numbers}\n`;
    Object.entries(errors.duplicate_phone_numbers).forEach(([phone, lines]) => {
      const lineText = formatLineNumbers(lines);
      text += `  ${phone} ${lineText}\n`;
    });
  }

  return text.trim();
};

const handleCopy = async () => {
  const text = formatErrorsAsText();
  if (!text) return;

  await copyTextToClipboard(text);
  useAlert(t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div>
    <div class="mb-4">
      <h3 class="text-lg font-medium">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.TITLE') }}
      </h3>
      <p class="mt-2 text-sm text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.DESCRIPTION') }}
      </p>
    </div>

    <div class="relative">
      <div
        class="max-h-[400px] overflow-y-auto p-4 border rounded-md bg-n-alpha-black2"
      >
        <Button
          variant="faded"
          color="slate"
          xs
          icon="i-lucide-copy"
          type="button"
          :title="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.COPY_ERRORS')"
          class="absolute top-2 ltr:right-2 rtl:left-2 z-10"
          @click="handleCopy"
        />
        <div class="flex flex-col gap-6">
          <div
            v-for="(detail, index) in errorDetails"
            :key="index"
            class="flex flex-col gap-3"
          >
            <p class="text-sm text-n-slate-12">
              {{ detail.message }}
            </p>
            <ul class="list-none ml-4 flex flex-col gap-1.5">
              <li
                v-for="item in detail.items"
                :key="item.phone"
                class="text-sm text-n-slate-11"
              >
                <span class="font-mono">{{ item.phone }}</span>
                <span class="text-n-slate-10 ml-2">{{
                  formatLineNumbers(item.lines)
                }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end mt-4">
      <Button
        variant="faded"
        color="neutral"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.CANCEL')"
        @click="emit('cancel')"
      />
    </div>
  </div>
</template>
